// ============================================================
// desempenho.js — o escore global e a classificação do atleta
//
// É o único lugar do painel que junta as duas avaliações num número só.
// A técnica vem de `avaliacaoTecnica.js`, a física de `testesFisicos.js`,
// e nenhuma das duas sabe da outra: a conta de juntar mora aqui, porque
// é ela que a coordenação lê no relatório e é ela que não pode divergir
// entre a tela, o PDF e a planilha.
//
// ── De onde saem os pesos ───────────────────────────────────
//
// A fórmula foi reconstruída da planilha de performance de 2026, e não
// inventada: 75% da média dos critérios técnicos mais 25% da média dos
// critérios de capacidade física. Ela reproduz os 90 escores globais da
// planilha, dos 30 atletas das três modalidades nas três avaliações de
// janeiro, abril e agosto, com arredondamento a uma decimal.
//
// Exemplo de conferência, o Adriano do BMX em janeiro: somatório técnico
// 47 em 6 critérios dá 7,83, somatório físico 31 em 4 critérios dá 7,75,
// e 0,75 × 7,83 mais 0,25 × 7,75 dá 7,81, que a planilha mostra como
// 7,8. A Carla, no mesmo dia, 31 em 6 e 26 em 4, dá 5,5.
//
// ── Por que o peso é 3 para 1 ───────────────────────────────
//
// O programa é de formação esportiva, e o que ele promete desenvolver é
// o gesto do esporte. A capacidade física entra porque sustenta o gesto,
// não porque seja o objetivo: um atleta que salta longe e não sabe fazer
// a curva não está indo bem no BMX. O peso é decisão da coordenação, e
// está aqui num lugar só para poder ser revisto sem caçar fórmula.
//
// ── O que o escore NÃO é ────────────────────────────────────
//
// Não é nota de aprovação, não é ranking de atleta e não decide quem
// viaja para competição. É um resumo de duas avaliações subjetivas, com
// o mesmo cuidado que a planilha original já dizia: a escala é de
// desempenho observado, com avaliadores diferentes por modalidade, e
// comparar atleta de BMX com atleta de tênis de mesa pelo global é
// comparar o olhar de dois técnicos.
// ============================================================

export const PESO_TECNICA = 0.75;
export const PESO_FISICA = 0.25;

// ── As faixas de classificação ───────────────────────────────
//
// São as cinco da planilha, com a descrição que a coordenação escreveu.
// `de` e `ate` são inclusivos, em cima do escore já arredondado a uma
// decimal, que é como ele aparece em todo lugar.

export const NIVEIS = [
  {
    id: "muitoAbaixo",
    rotulo: "Muito abaixo do ideal",
    de: 0,
    ate: 2.9,
    nivel: "0 a 2",
    descricao: "Desempenho muito abaixo do esperado.",
  },
  {
    id: "abaixoDaMedia",
    rotulo: "Abaixo da média",
    de: 3,
    ate: 4.9,
    nivel: "3 a 4",
    descricao:
      "Desempenho fraco, com falhas recorrentes. Algum potencial, mas precisa de grande "
      + "desenvolvimento.",
  },
  {
    id: "regular",
    rotulo: "Regular",
    de: 5,
    ate: 6.9,
    nivel: "5 a 6",
    descricao:
      "Desempenho aceitável. Cumpre as funções básicas, mas ainda com margem significativa "
      + "para evoluir.",
  },
  {
    id: "bom",
    rotulo: "Bom desempenho",
    de: 7,
    ate: 8.9,
    nivel: "7 a 8",
    descricao:
      "Desempenho sólido e confiável. Boa técnica e bom preparo. Contribui de forma "
      + "consistente.",
  },
  {
    id: "excelente",
    rotulo: "Excelente",
    de: 9,
    ate: 10,
    nivel: "9 a 10",
    descricao:
      "Desempenho excepcional. Atleta de alto nível, com domínio técnico, físico e mental. "
      + "Destaque constante.",
  },
];

export function nivelDoEscore(escore) {
  if (!Number.isFinite(escore)) return null;
  return NIVEIS.find(n => escore <= n.ate) || NIVEIS[NIVEIS.length - 1];
}

export function classificacaoDoEscore(escore) {
  const n = nivelDoEscore(escore);
  return n ? n.rotulo : null;
}

// ── O escore ─────────────────────────────────────────────────
//
// Recebe as duas metades já compiladas, cada uma com somatório e
// divisor. Devolve o global e as duas parcelas separadas, porque "6,4"
// sozinho não diz se o atleta está bem na técnica e mal no físico ou o
// contrário, e é essa a pergunta de quem vai montar o treino.
//
// ── Por que falta uma metade não vira meio escore ───────────
//
// Sem a técnica ou sem a física, o global não sai. A conta aritmética
// seria possível, bastaria renormalizar o peso que sobrou, e seria
// errada de um jeito difícil de perceber: o atleta que só tem avaliação
// técnica ganharia um global comparável com o de quem fez as duas,
// escondendo que metade da avaliação dele não existe. O relatório diz o
// que falta, em vez de preencher o buraco com o que tem.
export function escoreGlobal({ tecnica = null, fisica = null } = {}) {
  const mediaT = media(tecnica);
  const mediaF = media(fisica);
  const faltam = [];
  if (mediaT === null) faltam.push("avaliação técnica");
  if (mediaF === null) faltam.push("capacidades físicas");

  const global =
    mediaT === null || mediaF === null
      ? null
      : arred(PESO_TECNICA * mediaT + PESO_FISICA * mediaF, 1);
  const nivel = nivelDoEscore(global);

  return {
    global,
    classificacao: nivel ? nivel.rotulo : null,
    nivel,
    faltam,
    tecnica: parcela(tecnica, mediaT, PESO_TECNICA),
    fisica: parcela(fisica, mediaF, PESO_FISICA),
  };
}

// A média de uma metade sai do somatório dividido pelo divisor, e só cai
// na média pronta quando um dos dois não veio. É de propósito: a média
// pronta chega arredondada a duas casas, e arredondar antes de pesar
// desloca o global no limite da faixa, onde 6,95 e 6,94 viram
// classificações diferentes.
function media(metade) {
  if (!metade) return null;
  // Sem o `Number()` em volta, de propósito.
  //
  // `Number(null)` é 0, e 0 passa no Number.isFinite. Então uma metade com
  // somatório AUSENTE e divisor presente era lida como zero sobre o
  // divisor, dava média 0, e o global saía calculado: técnica 7 com a
  // bateria sem pontuar dava 5,3 em vez de "incompleto", e `faltam` saía
  // vazio, então nada avisava. Um quarto do escore virava nota zero por
  // causa de uma conversão.
  //
  // `parcela`, logo abaixo, já lia os dois campos sem converter. Agora as
  // duas leituras concordam: somatório que não é número é ausência.
  const soma = metade.somatorio;
  const divisor = metade.divisor;
  if (Number.isFinite(soma) && Number.isFinite(divisor) && divisor > 0) return soma / divisor;
  return Number.isFinite(metade.media) ? metade.media : null;
}

function parcela(metade, med, peso) {
  return {
    media: med === null ? null : arred(med, 2),
    somatorio: metade && Number.isFinite(metade.somatorio) ? metade.somatorio : null,
    divisor: metade && Number.isFinite(metade.divisor) ? metade.divisor : null,
    peso,
    // Quanto esta metade pôs no global. É a conta que o relatório mostra
    // ao lado do número, para o escore não ser um número que ninguém
    // sabe de onde veio.
    contribuicao: med === null ? null : arred(med * peso, 2),
  };
}

// ── A evolução do escore ─────────────────────────────────────
//
// A planilha acompanha duas janelas, de quatro e de oito meses, e mostra
// as duas em absoluto e em por cento. As duas formas ficam porque dizem
// coisas diferentes: meio ponto em cima de 2,8 é muito, e meio ponto em
// cima de 8,6 é quase o teto da escala.
export function evolucaoDoEscore(de, para) {
  const a = Number.isFinite(de) ? de : null;
  const b = Number.isFinite(para) ? para : null;
  if (a === null || b === null) return { absoluta: null, percentual: null };
  return {
    absoluta: arred(b - a, 1),
    percentual: a === 0 ? null : arred(((b - a) / a) * 100, 1),
  };
}

// A série de escores de um atleta, a partir das duas séries já
// compiladas. O casamento é por DATA, e é por isso que ele precisa de
// uma tolerância.
//
// ── Por que as duas datas não batem ─────────────────────────
//
// A avaliação técnica e os testes físicos são feitos na mesma semana e
// quase nunca no mesmo dia: em 2026, o tênis de mesa mediu em 18/08 e o
// BMX em 26/08, e dentro da mesma onda há dias diferentes por turma. Se
// o casamento exigisse data igual, o global de agosto não sairia para
// quase ninguém. A janela padrão de 45 dias cobre a onda inteira sem
// alcançar a onda seguinte, que está a quatro meses.
export const JANELA_DE_CASAMENTO_EM_DIAS = 45;

export function serieDeEscores({
  tecnica = [],
  fisica = [],
  janelaEmDias = JANELA_DE_CASAMENTO_EM_DIAS,
} = {}) {
  const usadas = new Set();
  const pontos = [...tecnica]
    .filter(t => t && t.data)
    .sort((a, b) => String(a.data).localeCompare(String(b.data)))
    .map(t => {
      const par = maisProxima(t.data, fisica, janelaEmDias, usadas);
      if (par) usadas.add(par.data);
      return montar(t, par);
    });

  // Teste físico sem avaliação técnica por perto também é um ponto da
  // série. Ele não tem global, e aparecer sem global é o que faz a
  // coordenação ir buscar a avaliação técnica que ficou para trás.
  const sobrando = fisica
    .filter(f => f && f.data && !usadas.has(f.data))
    .map(f => montar(null, f));

  const todos = [...pontos, ...sobrando].sort((a, b) =>
    String(a.data).localeCompare(String(b.data)));

  let primeiro = null;
  let anterior = null;
  return todos.map(p => {
    const comEvolucao = {
      ...p,
      desdeAAnterior: anterior ? evolucaoDoEscore(anterior.global, p.global) : null,
      desdeOInicio: primeiro ? evolucaoDoEscore(primeiro.global, p.global) : null,
      dataDoInicio: primeiro ? primeiro.data : p.data,
    };
    if (Number.isFinite(p.global)) {
      if (!primeiro) primeiro = p;
      anterior = p;
    }
    return comEvolucao;
  });
}

function montar(t, f) {
  const escore = escoreGlobal({
    tecnica: t ? { somatorio: t.somatorio, divisor: t.divisor, media: t.media } : null,
    fisica: f ? { somatorio: f.somatorio, divisor: f.divisor, media: f.media } : null,
  });
  return {
    // A data do ponto é a da avaliação técnica quando ela existe, porque
    // é a que pesa 75% e é a que a planilha usa para nomear a onda.
    data: (t && t.data) || (f && f.data) || null,
    dataTecnica: t ? t.data : null,
    dataFisica: f ? f.data : null,
    ...escore,
  };
}

function maisProxima(data, lista, janelaEmDias, usadas) {
  const alvo = emDias(data);
  if (alvo === null) return null;
  let escolhida = null;
  let menor = Infinity;
  lista.forEach(f => {
    if (!f || !f.data || usadas.has(f.data)) return;
    const d = emDias(f.data);
    if (d === null) return;
    const dist = Math.abs(d - alvo);
    if (dist <= janelaEmDias && dist < menor) {
      menor = dist;
      escolhida = f;
    }
  });
  return escolhida;
}

function emDias(iso) {
  const t = Date.parse(`${String(iso).slice(0, 10)}T00:00:00Z`);
  return Number.isFinite(t) ? Math.round(t / 86400000) : null;
}

// ── O quadro da modalidade ───────────────────────────────────
//
// A tabela que a planilha chama de classificação geral: uma linha por
// atleta, uma coluna por onda de avaliação, e a evolução do primeiro ao
// último escore. É a leitura da coordenação, e sai da mesma função que
// alimenta a ficha de cada atleta, para os dois números não divergirem.
export function quadroDaModalidade({ atletas = [] } = {}) {
  const linhas = atletas.map(a => {
    const serie = serieDeEscores({ tecnica: a.tecnica || [], fisica: a.fisica || [] });
    const comGlobal = serie.filter(p => Number.isFinite(p.global));
    const primeiro = comGlobal[0] || null;
    const ultimo = comGlobal[comGlobal.length - 1] || null;
    return {
      uid: a.uid,
      nome: a.nome,
      serie,
      atual: ultimo,
      evolucao: primeiro && ultimo && primeiro !== ultimo
        ? evolucaoDoEscore(primeiro.global, ultimo.global)
        : { absoluta: null, percentual: null },
      de: primeiro ? primeiro.data : null,
      para: ultimo ? ultimo.data : null,
      avaliacoesConsideradas: comGlobal.length,
    };
  });

  const atuais = linhas.map(l => (l.atual ? l.atual.global : null)).filter(Number.isFinite);
  const evolucoes = linhas.map(l => l.evolucao.percentual).filter(Number.isFinite);

  return {
    linhas: [...linhas].sort((a, b) => {
      const ga = a.atual && Number.isFinite(a.atual.global) ? a.atual.global : -1;
      const gb = b.atual && Number.isFinite(b.atual.global) ? b.atual.global : -1;
      if (ga !== gb) return gb - ga;
      return String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR");
    }),
    mediaAtual: atuais.length
      ? arred(atuais.reduce((s, v) => s + v, 0) / atuais.length, 2)
      : null,
    n: atuais.length,
    // A média das evoluções individuais, e não a evolução das médias. As
    // duas divergem quando atletas diferentes faltaram em ondas
    // diferentes, que é o caso em 2026.
    mediaDeEvolucao: evolucoes.length
      ? arred(evolucoes.reduce((s, v) => s + v, 0) / evolucoes.length, 1)
      : null,
  };
}

// ═══════════════════════════════════════════════════════════
// A frequência ao lado do desempenho
//
// A conta da frequência NÃO mora aqui. Ela é de `calculo.js`, mês a mês,
// com o teto de justificativa e as isenções que a coordenação fixou. O que
// mora aqui é a SOMA de meses já calculados e o recorte deles por onda de
// avaliação, que é o que põe as duas coisas na mesma linha.
//
// Somar os meses já calculados, e não recalcular o período inteiro de uma
// vez, é de propósito: o teto de justificativa é MENSAL, e um período
// calculado de uma vez abonaria meio semestre de falta para quem faltou
// tudo num mês só. Os números da ficha têm de ser os mesmos do relatório.
//
// ── As duas frequências ─────────────────────────────────────
//
// ABSOLUTA é presença sobre treino, sem perdoar nada. TOTAL soma os
// abonos: competição, dispensa de modalidade, viagem e o que a
// coordenação justificou. As duas juntas dizem uma coisa que nenhuma diz
// sozinha: 60% absoluta com 95% total é um atleta que quase não treinou e
// tinha motivo para isso, e é o caso de quem competiu o semestre inteiro.
// ═══════════════════════════════════════════════════════════

// Soma uma lista de meses já calculados.
//
// Cada mês entra como `{ ano, mes, treinos, presencas, abonos, faltas }`.
// Mês sem treino nenhum (férias, recesso) entra e não atrapalha: ele soma
// zero no numerador e zero no denominador.
export function somarFrequencia(meses) {
  const lista = (meses || []).filter(Boolean);
  const soma = campo => lista.reduce((total, m) => total + (Number(m[campo]) || 0), 0);
  const treinos = soma("treinos");
  const presencas = soma("presencas");
  const abonos = soma("abonos");
  const faltas = soma("faltas");
  return {
    meses: lista.length,
    comTreino: lista.filter(m => Number(m.treinos) > 0).length,
    treinos,
    presencas,
    abonos,
    faltas,
    // Sem treino no período não há percentual. Zero por cento diria que o
    // atleta faltou a tudo, e não houve a que faltar.
    pctAbs: treinos > 0 ? arred((100 * presencas) / treinos, 1) : null,
    pctTotal: treinos > 0
      ? arred(100 * Math.min(1, (presencas + abonos) / treinos), 1)
      : null,
  };
}

// A frequência de cada janela entre avaliações.
//
// Para a avaliação de agosto, o que interessa não é a frequência do ano
// inteiro: é a de abril a agosto, que é o treino que produziu aquela nota.
// A primeira onda leva tudo o que veio antes dela.
//
// O recorte é por MÊS, porque a frequência é mensal. Uma avaliação no dia
// 18 leva agosto inteiro, inclusive os treinos depois do dia 18: partir o
// mês ao meio daria um denominador que relatório nenhum tem, e o número
// deixaria de bater com a tela de frequência.
//
// Os meses DEPOIS da última avaliação não entram em janela nenhuma. Eles
// continuam no total do período, e é por isso que a soma das janelas pode
// ser menor que ele.
export function frequenciaPorOnda({ meses = [], datas = [] }) {
  const ordenadas = [...datas].filter(Boolean).sort();
  if (!ordenadas.length) return [];
  const chave = (ano, mes) => Number(ano) * 12 + Number(mes);
  const daData = iso => {
    const [a, m] = String(iso).slice(0, 10).split("-");
    return chave(Number(a), Number(m) - 1);
  };

  let piso = -Infinity;
  return ordenadas.map((data, i) => {
    const teto = daData(data);
    const doPeriodo = (meses || []).filter(m => {
      const k = chave(m.ano, m.mes);
      return k > piso && k <= teto;
    });
    piso = teto;
    return {
      data,
      // A primeira janela não tem começo declarado: ela é "tudo até aqui".
      desdeAInicio: i === 0,
      ...somarFrequencia(doPeriodo),
    };
  });
}

// A frequência e o escore global na mesma linha.
//
// Não há nota de corte nem fórmula ligando os dois, e não vai haver aqui:
// frequência alta não produz escore alto por si, e um atleta que treinou
// 95% e caiu de 7,5 para 7,0 é uma conversa com o técnico, não um
// diagnóstico do sistema. O que a ficha faz é pôr os dois números lado a
// lado, na mesma janela de tempo, e deixar a leitura com quem treina.
export function frequenciaComEscore({ meses = [], escores = [] }) {
  const comGlobal = (escores || []).filter(e => e && e.data);
  const janelas = frequenciaPorOnda({
    meses,
    datas: comGlobal.map(e => e.data),
  });
  return janelas.map((janela, i) => {
    const escore = comGlobal[i];
    const anterior = i > 0 ? comGlobal[i - 1] : null;
    return {
      ...janela,
      global: Number.isFinite(escore.global) ? escore.global : null,
      nivel: escore.nivel || null,
      // A variação do escore desde a onda anterior, em pontos. Em pontos
      // e não em porcentagem: a escala vai de 0 a 10 e meio ponto é meio
      // ponto, enquanto "mais 7%" sobre 7,0 não diz nada a quem treina.
      variacao: anterior && Number.isFinite(escore.global) && Number.isFinite(anterior.global)
        ? arred(escore.global - anterior.global, 1)
        : null,
    };
  });
}

function arred(n, casas = 1) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return null;
  const f = Math.pow(10, casas);
  return Math.round(Number(n) * f) / f;
}
